import React, { FC, memo, useEffect, useState } from "react";
import {
  Box,
  Button,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
} from "@mui/material";
import BaseTextArea from "components/BaseTextArea/BaseTextArea";
import { IOptionsSectionProps } from "./types";
import { IFormValues } from "components/BaseInput/types";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckIcon from "@mui/icons-material/Check";
import { useAppDispatch } from "store/hooks/useAppDispatch";
import { useAppSelector } from "store/hooks/useAppSelector";
import {
  setCheckedOptions,
  setCustomCheckedOptions,
} from "store/slices/options";
import { ICustomExtraCharge } from "store/api/types";
import { capitalizeFirstLetter, getRenderableOptions } from "./constants";
import { useFormik } from "formik";
import { shallowEqual } from "react-redux";
import { removeComment, setComment } from "store/slices/srcAddress";
import detailsStyles from "../DetailsSection/DetailsSection.styled";
import timeStyles from "pages/OrderModal/components/TimeSection/TimeSection.styled";
import styles from "./OptionsSection.styled";

const OptionsSection: FC<IOptionsSectionProps> = ({ onBackClick }) => {
  const options = useAppSelector((state) => state.options.options);
  const firstCustomOptions = useAppSelector(
    (state) => state.options.firstCustomOptions,
  );
  const customOptions = useAppSelector((state) => state.options.customOptions);
  const checkedOptions = useAppSelector(
    (state) => state.options.checkedOptions,
  );
  const customCheckedOptions = useAppSelector(
    (state) => state.options.customCheckedOptions,
  );
  const address = useAppSelector((state) => state.srcAddress, shallowEqual);
  const [selectedItems, setSelectedItems] = useState<string[]>(
    checkedOptions || [],
  );
  const [selectedCustomItems, setSelectedCustomItems] = useState<
    ICustomExtraCharge[]
  >(customCheckedOptions || []);
  const dispatch = useAppDispatch();

  const formik = useFormik<IFormValues>({
    initialValues: {
      comment: address.comment,
    },
    onSubmit: (values) => {
      console.log(values);
    },
  });

  useEffect(() => {
    if (formik.values.comment) {
      dispatch(setComment({ comment: formik.values.comment }));
    } else {
      dispatch(removeComment());
    }
  }, [formik.values.comment]);

  const handleItemClick = (value: string) => () => {
    setSelectedItems((prevSelected) =>
      prevSelected.includes(value)
        ? prevSelected.filter((item) => item !== value)
        : [...prevSelected, value],
    );
  };

  const handleCustomItemClick = (customOption: ICustomExtraCharge) => () => {
    setSelectedCustomItems((prevSelected) =>
      prevSelected.some((item) => item.Id === customOption.Id)
        ? prevSelected.filter((item) => item.Id !== customOption.Id)
        : [...prevSelected, customOption],
    );
  };

  const handleSaveClick = () => {
    dispatch(setCheckedOptions(selectedItems));
    dispatch(setCustomCheckedOptions(selectedCustomItems));
    onBackClick();
  };

  const renderCustomOptions = () =>
    !!customOptions.length && (
      <>
        {customOptions.map((customOption) => (
          <ListItemButton
            key={customOption.Id}
            selected={selectedCustomItems.some(
              (item) => item.Id === customOption.Id,
            )}
            onClick={handleCustomItemClick(customOption)}
            sx={styles.option(
              selectedCustomItems.some((item) => item.Id === customOption.Id),
            )}
          >
            <ListItemText primary={capitalizeFirstLetter(customOption.Name)} />
            <ListItemIcon
              sx={styles.icon(
                selectedCustomItems.some((item) => item.Id === customOption.Id),
              )}
            >
              <CheckIcon />
            </ListItemIcon>
          </ListItemButton>
        ))}
      </>
    );

  const renderOptions = () => {
    const renderableOptions = getRenderableOptions(options);
    return renderableOptions.map((option) => (
      <ListItemButton
        key={option.id}
        selected={selectedItems.includes(option.value)}
        onClick={handleItemClick(option.value)}
        sx={styles.option(selectedItems.includes(option.value))}
      >
        <ListItemText primary={capitalizeFirstLetter(option.title)} />
        <ListItemIcon sx={styles.icon(selectedItems.includes(option.value))}>
          <CheckIcon />
        </ListItemIcon>
      </ListItemButton>
    ));
  };

  return (
    <Box sx={styles.contentWrap}>
      <Box sx={{ ...timeStyles.headerWrap, ...styles.header }}>
        <IconButton onClick={handleSaveClick}>
          <ArrowBackIcon />
        </IconButton>
        <Typography component="h2" sx={timeStyles.title}>
          Опції замовлення
        </Typography>
      </Box>
      <Box sx={styles.optionsWrap}>
        <List sx={{ ...detailsStyles.listWrap, ...styles.listWrap }}>
          {firstCustomOptions ? (
            <>
              {renderCustomOptions()}
              {renderOptions()}
            </>
          ) : (
            <>
              {renderOptions()}
              {renderCustomOptions()}
            </>
          )}
        </List>
      </Box>
      <Box sx={styles.footer}>
        <BaseTextArea
          id="comment"
          name="comment"
          placeholder="Коментар для водія"
          formik={formik}
          initialValue={address.comment}
          maxLength={1024}
        />
        <Button onClick={handleSaveClick} sx={styles.btn} className="secondary">
          Зберегти
        </Button>
      </Box>
    </Box>
  );
};

export default memo(OptionsSection);
